import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent, CartSectionRestoredEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';
import {
  assertPogiAjaxCart,
  fetchPogiJsonWithTimeout,
  withPogiCartPresentationDeadlineAfterMutation,
  withPogiCartMutationLock,
} from '@pogi/cart-mutation';
import {
  prepareSavedLine, readSavedItems, writeSavedItems,
  reconcileSavedItems, rollbackSavedChange, savedIdentity,
} from '@pogi/cart-saved';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  /** @type {Set<Promise<void>>} */
  static #pendingQuantityUpdates = new Set();

  /**
   * Includes quantity mutations, their section renders, and control cleanup across all cart instances.
   * @returns {ReadonlyArray<Promise<void>>} A snapshot that cannot modify the shared pending set.
   */
  static get pendingQuantityUpdates() {
    return Array.from(CartItemsComponent.#pendingQuantityUpdates);
  }

  /**
   * Includes custom full-cart mutations in the same presentation barrier as drawer quantities.
   * The marker never rejects; the caller keeps the original result and error semantics.
   * @template T
   * @param {Promise<T>} pending
   * @returns {Promise<T>}
   */
  static trackPendingUpdate(pending) {
    const completion = Promise.resolve(pending).then(() => {}, () => {});
    CartItemsComponent.#pendingQuantityUpdates.add(completion);
    completion.then(() => CartItemsComponent.#pendingQuantityUpdates.delete(completion));
    return pending;
  }

  // Opening a pending drawer is feedback, not a view of the previous cart.
  dispatchViewEvent() {
    if (this.closest('theme-drawer')?.hasAttribute('data-pending-add')) return;
    super.dispatchViewEvent();
  }

  #pendingQuantityChanges = new Map();
  #pendingLineMutations = 0;
  #savingLineKeys = new Set();
  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;

  /**
   * Capture the focused line control before a section morph. Shopify's cart
   * response remains authoritative; this snapshot only restores keyboard
   * position after the new DOM has been committed.
   * @param {number} line
   */
  #captureLineFocus(line) {
    const row = this.refs.cartItemRows?.[line - 1];
    const active = document.activeElement;
    if (!(row instanceof HTMLElement) || !(active instanceof HTMLElement) || !row.contains(active)) return null;

    let selector = '';
    if (active.matches('.cart-items__save')) selector = '.cart-items__save';
    else if (active.matches('.cart-items__remove')) selector = '.cart-items__remove';
    else if (active.matches('button[name="minus"]')) selector = 'button[name="minus"]';
    else if (active.matches('button[name="plus"]')) selector = 'button[name="plus"]';
    else if (active.matches('input[name="updates[]"]')) selector = 'input[name="updates[]"]';

    return {
      key: row.dataset.key || '',
      index: Array.from(this.refs.cartItemRows || []).indexOf(row),
      selector,
    };
  }

  /**
   * Restore focus to the equivalent control, a surviving adjacent row, or the
   * empty-cart action after the authoritative section render settles.
   * @param {{key: string, index: number, selector: string} | null} snapshot
   */
  #restoreLineFocus(snapshot) {
    if (!snapshot) return;

    const section = document.getElementById(`shopify-section-${this.sectionId}`);
    const component = this.isConnected
      ? this
      : section?.querySelector('cart-items-component');
    if (!(component instanceof HTMLElement)) return;

    const rows = Array.from(component.querySelectorAll('[ref="cartItemRows[]"]'));
    const keyedRow = rows.find((row) => row instanceof HTMLElement && row.dataset.key === snapshot.key);
    const fallbackIndex = Math.min(Math.max(snapshot.index, 0), rows.length - 1);
    const targetRow = keyedRow || rows[fallbackIndex];
    let target = null;

    if (targetRow instanceof HTMLElement) {
      const preferred = snapshot.selector ? targetRow.querySelector(snapshot.selector) : null;
      if (
        preferred instanceof HTMLElement &&
        !preferred.matches(':disabled, [hidden]') &&
        !preferred.closest('[hidden]')
      ) {
        target = preferred;
      }
      target ||= targetRow.querySelector(
        '.cart-items__remove:not([disabled]):not([hidden]), button:not([disabled]):not([hidden]), input:not([disabled]):not([hidden]), a[href]:not([hidden])'
      );
    }

    if (!target && snapshot.selector === '.cart-items__save') {
      target = component.querySelector('[data-pogi-drawer-saved-link]:not([hidden])');
    }
    target ||= component.querySelector('.cart-items__empty-button');
    target ||= section?.querySelector('#cart-drawer-heading, h1, h2');
    if (
      !(target instanceof HTMLElement) ||
      target.matches(':disabled, [hidden]') ||
      target.closest('[hidden]')
    ) return;
    if (!target.matches('a[href], button, input, select, textarea, [tabindex]')) target.tabIndex = -1;
    target.focus({ preventScroll: true });
  }

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh — running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    const external = this.#isExternalCartUpdate(event);
    event.promise
      ?.then(async ({ detail }) => {
        if (!external) return;
        const sectionsHtml = detail?.sections?.[this.sectionId];
        if (sectionsHtml) {
          sectionRenderer.abortRender(this.sectionId);
          await morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.#updateCartQuantitySelectorButtonStates();
        } else if (external) {
          // External caller (Shopify.actions.updateCart or SFAPI default handler) didn't
          // attach sections; refetch so the discount UI reflects the post-mutation cart.
          // Internal cart-discount-component morphs the section itself — no fallback needed.
          await sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    // Internal cart-note dispatches don't need a section refresh — the user typed the
    // value and the textarea retains it. Only external callers need the UI synced.
    if (!this.#isExternalCartUpdate(event)) return;
    event.promise
      ?.then(async ({ detail }) => {
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = sections?.[this.sectionId];
        if (sectionsHtml) {
          sectionRenderer.abortRender(this.sectionId);
          await morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          await sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityChange);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    window.addEventListener('pageshow', this.#handlePageShow);
    this.#updateSavedAccess();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#updateSavedAccess();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityChange);
    for (const pending of this.#pendingQuantityChanges.values()) pending.cancel();
    this.#pendingQuantityChanges.clear();
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    window.removeEventListener('pageshow', this.#handlePageShow);
  }

  /**
   * Re-renders the cart section when the page is restored from the back/forward cache.
   *
   * A restored page replays a frozen DOM, so the line items still show whatever the cart
   * held before the shopper navigated away. `cart-icon.js` self-corrects its count bubble
   * on this same event, which is why the bubble and the cart body disagree until a reload.
   *
   * `persisted` alone does not cover Chrome. Chrome refuses the bfcache for any document holding
   * the Sign in with Shop iframe, `/services/login_with_shop/authorize` inside the shadow root of
   * `shopify-account > shop-login`, and every product page carries one. A shopper's Back there
   * rebuilds the document from the HTTP disk cache instead: `persisted` is false and the frozen
   * cart markup renders anyway. Only the navigation entry separates that from an ordinary page
   * view, so it is the second channel. Safari and Firefox do restore the same page and arrive
   * through `persisted`. Measured on os3 across all three browsers, 2026-09-03.
   *
   * Both channels are still needed. A restored document keeps its ORIGINAL navigation entry in
   * Chrome and Safari, reporting `navigate`, so `back_forward` alone would miss every real
   * restore. And `pageshow` fires after `load` on every ordinary navigation, so dropping both
   * guards would put a section fetch on every page view.
   *
   * @param {PageTransitionEvent} event
   */
  #handlePageShow = (event) => {
    const navigationEntry = globalThis.performance?.getEntriesByType?.('navigation')[0];
    const navigationType = CartItemsComponent.#isNavigationTiming(navigationEntry) ? navigationEntry.type : undefined;
    if (!event.persisted && navigationType !== 'back_forward') return;
    // `section_id` is optional in `snippets/cart-items-component.liquid` and the `sectionId`
    // getter throws without it. Every other caller reads it inside a promise chain that
    // already has a catch; this one runs straight off a window listener, so it would escape.
    if (!this.dataset.sectionId) return;

    // `cache: false` on both paths. A bfcache restore never fires `load`, so the renderer's cache
    // still holds the pre-navigation copy. A disk-cache Back does fire `load` and seeds the cache
    // from the stale document it just rebuilt. Either way the cached copy is the same stale HTML
    // the DOM already shows.
    sectionRenderer
      .renderSection(this.sectionId, {
        cache: false,
        mode: this.isDrawer ? 'hydration' : 'full',
        // Empty → non-empty adds the cart summary markup, which carries its own stylesheet.
        injectStylesheet: this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null,
      })
      .then(() => {
        // Morph swaps the quantity selectors in place, so they never reconnect and their
        // min/max button states stay bound to the pre-restore quantities. A line restored to its
        // minimum would otherwise keep minus enabled: a wrong affordance and a wasted cart mutation.
        this.#updateCartQuantitySelectorButtonStates();
        this.#updateSavedAccess();
        // No drawer-open event fires on a restore, so the sticky summary measurement has to
        // be driven from here. `cart-drawer-component` listens for this.
        this.dispatchEvent(new CartSectionRestoredEvent());
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] bfcache restore render failed:', error);
      });
  };

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange = (event) => {
    if (!(event instanceof QuantitySelectorUpdateEvent)) return;
    if (!(event.target instanceof HTMLElement) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;
    const row = event.target.closest('[ref="cartItemRows[]"]');
    const key = row?.dataset.key?.trim();
    if (!line || !key || !this.refs.cartItemRows.includes(row)) return;
    if (this.#savingLineKeys.has(key)) return;
    if (!Number.isSafeInteger(quantity) || quantity < 0) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line, event);
    }

    // Resolve identity before waiting: an earlier row can disappear while its
    // request is in flight. Different products must never cancel one another.
    let pending = this.#pendingQuantityChanges.get(key);
    if (!pending) {
      pending = debounce((nextQuantity) => {
        this.#pendingQuantityChanges.delete(key);
        const currentRow = this.refs.cartItemRows.find((item) => item.dataset.key === key);
        if (!currentRow) return;
        const currentLine = this.refs.cartItemRows.indexOf(currentRow) + 1;
        this.updateQuantity({ line: currentLine, key, quantity: nextQuantity, action: 'change' });
        const textComponent = /** @type {TextComponent | undefined} */ (currentRow.querySelector('text-component'));
        textComponent?.shimmer();
      }, 300);
      this.#pendingQuantityChanges.set(key, pending);
    }
    pending.quantity = quantity;
    pending(quantity);
  };

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   * @param {Event} [event] - The delegated control event, when present.
   */
  onLineItemRemove(line, event) {
    const eventRow = event?.target instanceof HTMLElement
      ? event.target.closest('[ref="cartItemRows[]"]')
      : null;
    const cartItemRowToRemove = eventRow && this.contains(eventRow)
      ? eventRow
      : this.refs.cartItemRows[line - 1];
    const key = cartItemRowToRemove?.dataset.key?.trim() || '';
    if (this.#savingLineKeys.has(key)) return;
    const rowsToRemove = cartItemRowToRemove
      ? [cartItemRowToRemove, ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === key)]
      : [];
    for (const row of rowsToRemove) {
      this.#pendingQuantityChanges.get(row.dataset.key)?.cancel();
      this.#pendingQuantityChanges.delete(row.dataset.key);
    }
    this.updateQuantity({
      line: cartItemRowToRemove ? this.refs.cartItemRows.indexOf(cartItemRowToRemove) + 1 : line,
      key,
      quantity: 0,
      action: 'clear',
    });

    if (!cartItemRowToRemove) return;

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        document.getElementById('cart-drawer-heading')?.remove();
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /** Save uses the normal drawer mutation/presentation path, without optimistic removal. */
  onLineItemSaveForLater(line, event) {
    event?.preventDefault();
    const row = event?.target instanceof HTMLElement
      ? event.target.closest('[ref="cartItemRows[]"]')
      : this.refs.cartItemRows?.[line - 1];
    const key = row?.dataset.key?.trim();
    if (!row || !key || !this.contains(row) || this.#pendingLineMutations > 0) return;
    if (row.dataset.parentKey || this.refs.cartItemRows.some((item) => item.dataset.parentKey === key)) return;
    const pending = this.#pendingQuantityChanges.get(key);
    const savedQuantity = pending?.quantity;
    pending?.cancel();
    this.#pendingQuantityChanges.delete(key);
    return this.updateQuantity({
      line: this.refs.cartItemRows.indexOf(row) + 1,
      key, quantity: 0, action: 'save', saveForLater: true, savedQuantity,
    });
  }

  #updateSavedAccess(error = false) {
    const hasSaved = readSavedItems().length > 0;
    for (const link of this.querySelectorAll('[data-pogi-drawer-saved-link]')) link.hidden = !hasSaved;
    const status = this.querySelector('[data-pogi-drawer-save-status]');
    const errorText = this.querySelector('[data-pogi-drawer-save-error]');
    if (errorText) errorText.hidden = !error;
    if (status) {
      status.hidden = !hasSaved && !error;
      status.setAttribute('role', error ? 'alert' : 'status');
    }
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {string} [config.key] - Stable identity captured by the originating control.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @param {boolean} [config.saveForLater] - Persist this exact line before removal.
   */
  updateQuantity(config) {
    let completeQuantityUpdate;
    const quantityUpdate = new Promise((resolve) => { completeQuantityUpdate = resolve; });
    CartItemsComponent.#pendingQuantityUpdates.add(quantityUpdate);
    const complete = () => {
      CartItemsComponent.#pendingQuantityUpdates.delete(quantityUpdate);
      completeQuantityUpdate();
    };
    try {
      return Promise.resolve(this.#performQuantityUpdate(config)).finally(complete);
    } catch (error) {
      // Synchronous setup failures must not strand a discount render forever.
      complete();
      throw error;
    }
  }

  #performQuantityUpdate(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    const { line, quantity } = config;
    const { cartTotal } = this.refs;
    const focusSnapshot = this.#captureLineFocus(line);
    const lineId = config.key ?? this.refs.cartItemRows[line - 1]?.dataset.key?.trim() ?? '';
    if (!lineId) {
      const message = Theme.translations.cart_update_error || 'Unable to update your cart. Please try again.';
      const errorText = this.refs[`cartItemError-${line}`];
      const errorContainer = this.refs[`cartItemErrorContainer-${line}`];
      if (errorText instanceof HTMLElement) errorText.textContent = message;
      if (errorContainer instanceof HTMLElement) errorContainer.classList.remove('hidden');
      this.dispatchEvent(
        new CartErrorEvent({
          error: message,
          code: 'INVALID',
          detail: { reason: 'missing-line-key' },
        })
      );
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      return;
    }

    this.#disableCartItems();

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    for (const sectionId of sectionsToUpdate) sectionRenderer.abortRender(sectionId);

    const body = JSON.stringify({
      id: lineId,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: config.action === 'change' && quantity > 0 ? 'update' : 'remove',
        context: 'cart',
        lines: [{ id: lineId, quantity }],
        promise: deferredUpdatePromise.promise,
      })
    );

    let sectionRecoveryFailed = false;
    let saveState = 'preflight';
    let preparedSave = null;
    let saveConfirmed = false;
    const confirmSaved = (cart) => {
      const saved = reconcileSavedItems(cart, true);
      return saved.some((entry) => savedIdentity(entry) === savedIdentity(preparedSave?.entry)
        && !entry.pendingCartChange && entry.quantity >= preparedSave.entry.quantity);
    };
    if (config.saveForLater) this.#savingLineKeys.add(lineId);
    this.#pendingLineMutations += 1;
    return withPogiCartMutationLock(async () => {
      if (config.saveForLater) {
        const beforeCart = await this.fetchCartData(true);
        preparedSave = prepareSavedLine(beforeCart, lineId, {}, config.savedQuantity);
        writeSavedItems(preparedSave.saved);
      }
      saveState = 'unknown';
      return fetchPogiJsonWithTimeout(
        `${Theme.routes.cart_change_url}`,
        fetchConfig('json', { body }),
      ).then(async ({ response, payload }) => {
        if (!payload || typeof payload !== 'object') throw new Error('Cart response was malformed');
        const parsedResponseText = payload;

        resetShimmer(this);

        if (parsedResponseText.errors) {
          if (config.saveForLater) {
            saveState = 'rejected';
            throw new Error('Cart save was rejected');
          }
          this.#handleCartError(line, parsedResponseText);
          deferredUpdatePromise.reject(new Error(parsedResponseText.errors));
          return;
        }
        if (!response.ok) {
          saveState = 'rejected';
          throw new Error(`Cart request failed (${response.status})`);
        }
        assertPogiAjaxCart(parsedResponseText);
        if (config.saveForLater) {
          saveConfirmed = confirmSaved(parsedResponseText);
          if (!saveConfirmed) throw new Error('Saved line removal is not confirmed');
          saveState = 'committed';
        }
        const sectionHtml = parsedResponseText.sections?.[this.sectionId];
        if (typeof sectionHtml !== 'string' || sectionHtml.trim() === '') {
          throw new Error('Cart response did not include the requested section');
        }

        const newSectionHTML = new DOMParser().parseFromString(
          sectionHtml,
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        sectionRenderer.abortRender(this.sectionId);
        await morphSection(this.sectionId, sectionHtml, {
          mode: this.isDrawer ? 'hydration' : 'full',
        });

        this.#updateCartQuantitySelectorButtonStates();
        if (config.saveForLater) this.#updateSavedAccess();

        deferredUpdatePromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText),
          detail: {
            sections: parsedResponseText.sections,
            items: parsedResponseText.items,
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            didError: false,
          },
        });
      });
    })
      .catch(async (error) => {
        console.error(error);
        resetShimmer(this);
        let currentCart = null;
        if (config.saveForLater && preparedSave && (saveState === 'preflight' || saveState === 'rejected')) {
          try { rollbackSavedChange(preparedSave.transactionId); } catch (_) { /* Durable marker stays recoverable. */ }
        }
        try {
          currentCart = await this.fetchCartData(config.saveForLater === true);
          if (config.saveForLater && preparedSave && saveState !== 'preflight' && saveState !== 'rejected') {
            try { saveConfirmed = confirmSaved(currentCart); } catch (_) { /* Keep the durable pending copy. */ }
          }
          deferredUpdatePromise.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(currentCart),
            detail: {
              sections: currentCart.sections,
              items: currentCart.items,
              itemCount: currentCart.item_count,
              source: 'cart-items-component',
              didError: !saveConfirmed,
              recovered: true,
              commitState: saveConfirmed ? 'committed' : config.saveForLater ? saveState : 'unknown',
            },
          });
        } catch (_) {
          deferredUpdatePromise.reject(error);
        }

        if (!saveConfirmed) this.dispatchEvent(
          new CartErrorEvent({
            error: Theme.translations.cart_update_error || 'Unable to update your cart. Please try again.',
            code: 'SERVICE_UNAVAILABLE',
            detail: {
              technicalMessage: error?.message,
              recoveredCart: Boolean(currentCart),
            },
          })
        );
        try {
          await this.recoverSection();
        } catch (sectionError) {
          sectionRecoveryFailed = true;
          this.dataset.sectionRecoveryFailed = 'true';
          console.warn('[cart-items] Section recovery failed:', sectionError);
        }
      })
      .finally(() => {
        this.#pendingLineMutations -= 1;
        if (config.saveForLater) {
          this.#savingLineKeys.delete(lineId);
          this.#updateSavedAccess(!saveConfirmed || sectionRecoveryFailed);
        }
        if (!sectionRecoveryFailed) this.#enableCartItems();
        this.#restoreLineFocus(focusSnapshot);
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    const rawError = parsedResponseText.errors;
    const message =
      parsedResponseText.description ||
      parsedResponseText.message ||
      (typeof rawError === 'string' ? rawError : Object.values(rawError || {}).flat().join(', ')) ||
      window.POGI_I18N?.cart_update_error ||
      'Sorry, we could not update that quantity. Please try again.';
    if (typeof window.POGI_SET_TRANSLATED_TEXT === 'function') {
      window.POGI_SET_TRANSLATED_TEXT(cartItemError, message);
    } else {
      cartItemError.textContent = message;
    }
    cartItemErrorContainer.classList.remove('hidden');

    this.dispatchEvent(
      new CartErrorEvent({
        error: parsedResponseText.errors || 'Cart update failed',
        code: 'INVALID',
      })
    );
  };

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    const rawSectionRenderPromise = event.promise
      ?.then(async ({ detail }) => {
        if (detail?.skipSectionRefresh) return true;
        const sections = detail?.sections;
        const cartItemsHtml = sections?.[this.sectionId];
        // Animate empty → non-empty in the drawer (possible in squeeze mode
        // where the page is interactive alongside the open drawer). This also
        // needs the response stylesheet because it adds the cart summary markup.
        const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
        const drawer = this.closest('theme-drawer');
        const isCartDrawerOpen = drawer?.hasAttribute('open') === true;
        const isCartPending = drawer?.hasAttribute('data-pending-add') === true;
        /** @type {'hydration' | 'full'} */
        const mode = this.isDrawer ? 'hydration' : 'full';
        const morphOptions = {
          mode,
          injectStylesheet: wasEmptyCartDrawer,
        };

        if (cartItemsHtml) {
          sectionRenderer.abortRender(this.sectionId);
          const existingKeys = new Set(this.refs.cartItemRows?.map((row) => row.dataset.key) ?? []);

          if (wasEmptyCartDrawer && isCartDrawerOpen && !isCartPending) {
            await startViewTransition(
              () => morphSection(this.sectionId, cartItemsHtml, morphOptions),
              ['fill-cart-drawer'],
            );
          } else {
            await morphSection(this.sectionId, cartItemsHtml, morphOptions);
          }

          // Animate newly added rows (reverse of the remove animation).
          if (!wasEmptyCartDrawer && !prefersReducedMotion()) {
            for (const row of this.refs.cartItemRows ?? []) {
              if (!existingKeys.has(row.dataset.key)) {
                row.classList.add('adding');
                onAnimationEnd(row, () => row.classList.remove('adding'));
              }
            }
          }

          // Update button states for all cart quantity selectors after morph
          this.#updateCartQuantitySelectorButtonStates();
        } else {
          if (event.action === 'add') {
            throw new Error('Cart add response did not include the requested drawer section');
          }
          await sectionRenderer.renderSection(this.sectionId, { cache: false, ...morphOptions });
        }

        return true;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
        return false;
      });

    const sectionRenderPromise = withPogiCartPresentationDeadlineAfterMutation(
      event.promise,
      rawSectionRenderPromise,
    );

    // CartDrawerComponent waits for this before opening. This is deliberately
    // view-only metadata on the already-standard Shopify cart event; producers
    // still own only action/context/lines/promise.
    if (this.isDrawer) event.pogiSectionRenderPromise = sectionRenderPromise;
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    if (this.#pendingLineMutations > 0 || this.dataset.sectionRecoveryFailed === 'true') return;
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Performs a bounded, view-only recovery of this rendered cart section.
   * It never owns the cart mutation lock, so a failed section request cannot
   * block later cart writes. The drawer retries this on its next open.
   */
  async recoverSection() {
    const url = new URL(window.location.href);
    url.searchParams.set('sections', this.sectionId);
    url.hash = '';
    const { response, payload } = await fetchPogiJsonWithTimeout(url.toString(), {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store',
    });
    if (!response.ok) throw new Error(`Cart section refresh failed (${response.status})`);
    const html = payload?.[this.sectionId];
    if (typeof html !== 'string' || html.trim() === '') {
      throw new Error('Cart section refresh returned no HTML');
    }
    await morphSection(this.sectionId, html, {
      mode: this.isDrawer ? 'hydration' : 'full',
    });
    delete this.dataset.sectionRecoveryFailed;
    this.#enableCartItems();
    this.#updateCartQuantitySelectorButtonStates();
    return true;
  }

  async fetchCartData(fresh = false) {
    const load = async () => {
      const { response, payload } = await fetchPogiJsonWithTimeout(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store',
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
      return assertPogiAjaxCart(payload);
    };
    if (fresh) return load();
    if (this.#pendingCartFetch) return this.#pendingCartFetch;
    this.#pendingCartFetch = load().finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
  /**
   * `getEntriesByType` is typed as the `PerformanceEntry` base, which has no `type`, and the DOM
   * lib has no per-string overload for `'navigation'`. `entryType` is the spec's discriminant and
   * lives on the base, so this narrows at runtime instead of asserting the way a cast would.
   *
   * @param {PerformanceEntry | undefined} entry
   * @returns {entry is PerformanceNavigationTiming}
   */
  static #isNavigationTiming(entry) {
    return entry?.entryType === 'navigation';
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
